/**
 * PO Form Component
 *
 * The main form component for creating and editing Purchase Orders.
 * Combines all field sections (Header, Parties, Items) into a single form.
 *
 * Used in:
 * - POReviewer page (for reviewing AI-extracted data)
 * - PO edit views
 *
 * Features:
 * - React Hook Form integration
 * - Confidence-based highlighting for AI-extracted fields
 * - Validation
 * - Submit handling
 */

import { useForm } from 'react-hook-form';
import { useEffect, forwardRef, useImperativeHandle } from 'react';
import POHeaderFields from './POHeaderFields';
import POPartiesFields from './POPartiesFields';
import POItemsFields from './POItemsFields';
import POSectionsFields from './POSectionsFields';
import Button from '../common/Button';
import { isFieldEmpty, resolveFieldValue, normalizeConfidence, getFieldScore, unwrapEvaluation } from '../../utils/confidenceUtils';

/**
 * Default values for a new PO form
 */
const defaultValues = {
  header: {
    poNumber: '',
    date: '',
    currency: 'USD',
    paymentTerms: '',
    incoterms: '',
    incotermsLocation: '',
    requiredBy: ''
  },
  parties: [],
  items: [],
  sections: [],
  totals: { subtotal: null, tax: null, shipping: null, discount: null, grandTotal: null }
};

/**
 * POForm Component
 *
 * @param {Object} props - Component props
 * @param {Object} [props.initialData] - Initial form data (from AI extraction)
 * @param {Object} [props.evaluation] - LLM-as-judge evaluation result (new format)
 * @param {Object} [props.confidence] - AI confidence scores for each field (legacy format)
 * @param {Function} props.onSubmit - Called when form is submitted
 * @param {boolean} [props.isSubmitting] - Show loading state on submit button
 * @param {boolean} [props.hideActions] - Whether to hide the bottom action buttons (for use when triggering submit externally)
 *
 * @example
 * <POForm
 *   initialData={extractedData}
 *   confidence={confidenceScores}
 *   onSubmit={handleSave}
 *   isSubmitting={isSaving}
 * />
 */
const POForm = forwardRef(function POForm({
  initialData,
  evaluation: rawEvaluation,
  confidence = {},
  issues = [],
  onSubmit,
  isSubmitting = false,
  submitLabel = 'Save',
  disabled = false,
  hideActions = false
}, ref) {
  const evaluation = unwrapEvaluation(rawEvaluation);

  // Debug: Log what data we receive
  console.log('=== POForm MOUNT/UPDATE ===');
  console.log('initialData received:', JSON.stringify(initialData, null, 2));
  console.log('initialData.parties:', initialData?.parties);
  console.log('Using defaultValues:', initialData ? 'from initialData' : 'from defaultValues constant');
  console.log('===========================');

  // Merge initial data with defaults to ensure all required fields exist
  const mergedDefaults = initialData ? {
    header: { ...defaultValues.header, ...initialData.header },
    parties: initialData.parties || [],
    items: initialData.items || [],
    sections: initialData.sections || [],
    totals: { ...defaultValues.totals, ...initialData.totals }
  } : defaultValues;

  console.log('=== MERGED DEFAULTS ===');
  console.log('mergedDefaults:', JSON.stringify(mergedDefaults, null, 2));
  console.log('=======================');

  // Initialize react-hook-form with merged data
  const form = useForm({
    defaultValues: mergedDefaults,
    mode: 'onBlur' // Validate on blur
  });

  // Reset form when initialData changes (e.g. when switching between POs or re-extracting)
  useEffect(() => {
    console.log('POForm: initialData changed, resetting form with:', JSON.stringify(mergedDefaults, null, 2));
    form.reset(mergedDefaults);
  }, [JSON.stringify(initialData), form]); // Deep comparison via stringify to avoid loops

  /**
   * Handle form submission
   * Wraps the onSubmit prop to handle any form-level logic
   */
  const handleSubmit = form.handleSubmit(async (data) => {
    try {
      await onSubmit?.(data);
    } catch (error) {
      // Error handling is done by the parent component
      console.error('Form submission error:', error);
    }
  });

  // Expose submit method to parent via ref
  useImperativeHandle(ref, () => ({
    submit: () => {
      handleSubmit();
    }
  }));

  // Check if any displayed field has a low score (below 0.8).
  // Checks the LLM-as-judge evaluation first (new format), then falls back
  // to legacy confidence scores for backward compatibility.
  const checkLowConfidence = () => {
    const formValues = form.getValues();

    // Check scalar header/totals keys via evaluation first, then legacy
    const scalarKeys = [
      'header.poNumber', 'header.date', 'header.currency',
      'header.paymentTerms', 'header.incoterms', 'header.incotermsLocation',
      'header.requiredBy',
      'totals.subtotal', 'totals.tax', 'totals.grandTotal'
    ];
    for (const key of scalarKeys) {
      const score = getFieldScore(evaluation, confidence, key);
      if (score == null) continue;
      const val = resolveFieldValue(formValues, key);
      if (normalizeConfidence(score, isFieldEmpty(val)) < 0.8) return true;
    }

    // Check evaluation field_scores for parties and items (new format)
    if (evaluation?.field_scores) {
      for (const [path, fieldData] of Object.entries(evaluation.field_scores)) {
        const score = typeof fieldData === 'object' ? fieldData.score : fieldData;
        if (score != null && score < 0.8) return true;
      }
      // If evaluation exists, skip legacy checks below
      return false;
    }

    // Legacy: Check sections — scalar confidence
    const sectionsConf = confidence.sections;
    if (sectionsConf !== undefined) {
      const sectionsEmpty = (formValues.sections || []).length === 0;
      if (normalizeConfidence(sectionsConf, sectionsEmpty) < 0.8) return true;
    }

    // Legacy: Check parties — array or scalar
    const partiesConf = confidence.parties;
    if (Array.isArray(partiesConf)) {
      const partiesData = formValues.parties || [];
      for (let i = 0; i < partiesConf.length; i++) {
        const partyObj = partiesConf[i];
        if (!partyObj || typeof partyObj !== 'object') continue;
        for (const [key, conf] of Object.entries(partyObj)) {
          if (typeof conf !== 'number') continue;
          const val = resolveFieldValue(partiesData[i], key);
          if (normalizeConfidence(conf, isFieldEmpty(val)) < 0.8) return true;
        }
      }
    } else if (typeof partiesConf === 'number' && partiesConf < 0.8) {
      return true;
    }

    // Legacy: Check items — array or scalar
    const itemsConf = confidence.items;
    if (Array.isArray(itemsConf)) {
      const itemsData = formValues.items || [];
      for (let i = 0; i < itemsConf.length; i++) {
        const itemObj = itemsConf[i];
        if (!itemObj || typeof itemObj !== 'object') continue;
        for (const [key, conf] of Object.entries(itemObj)) {
          if (typeof conf !== 'number') continue;
          const val = itemsData[i]?.[key];
          if (normalizeConfidence(conf, isFieldEmpty(val)) < 0.8) return true;
        }
      }
    } else if (typeof itemsConf === 'number' && itemsConf < 0.8) {
      return true;
    }

    return false;
  };

  const hasLowConfidenceFields = checkLowConfidence();

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Low confidence warning */}
      {hasLowConfidenceFields && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex">
            <svg
              className="h-5 w-5 text-amber-400 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div>
              <h4 className="text-sm font-medium text-amber-800">
                Some fields need your attention
              </h4>
              <p className="text-sm text-amber-700 mt-1">
                Fields highlighted in yellow have lower AI confidence scores.
                Please verify these values against the source document.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header Section */}
      <POHeaderFields form={form} evaluation={evaluation} confidence={confidence} issues={issues} />

      {/* Parties Section */}
      <POPartiesFields form={form} evaluation={evaluation} confidence={confidence} issues={issues} />

      {/* Items Section */}
      <POItemsFields form={form} evaluation={evaluation} confidence={confidence} issues={issues} />

      {/* Document Sections (Terms & Conditions, Special Instructions, etc.) */}
      <POSectionsFields form={form} evaluation={evaluation} confidence={confidence} issues={issues} />

      {/* Form Actions */}
      {!hideActions && (
        <div className="flex justify-end space-x-3 pt-6 border-t">
          {/* Save as Draft button (optional - could be added) */}
          {/* <Button type="button" variant="secondary">
            Save as Draft
          </Button> */}

          {/* Submit button */}
          <Button
            type="submit"
            variant="success"
            size="lg"
            loading={isSubmitting}
            disabled={disabled}
          >
            {isSubmitting ? 'Saving...' : submitLabel}
          </Button>
        </div>
      )}
    </form>
  );
});

export default POForm;
